import React, { ComponentType, CSSProperties, MouseEventHandler } from 'react';
import { is } from 'immutable';
import { areEqual } from 'react-window';

import { LockScroll } from 'flow/components/ContextMenu';
import FoldableSectionTitle from 'flow/components/nav/list-items/FoldableSectionTitle';
import ListRowFrame from 'flow/components/nav/list-keys/ListRowFrame';
import {
    RowStore,
    useStoreHoldsRow,
} from 'flow/components/nav/list-keys/row-index-store';
import { RowMarking } from 'flow/components/ui-components/BaseListItem';
import useStableCallback from 'flow/hooks/useStableCallback';
import {
    DepartmentState,
    PbxListItem,
    PbxListRow as Row,
    rowIsFocusable,
} from './pbx-list-rows';

export interface PbxListItemData {
    rows: Row[];
    // Holds the key of the row that shows the selection. A favourite queue is
    // in two rows, and only one shows it.
    selectionStore: RowStore<string>;
    tabStopStore: RowStore<string>;
    /** One key for each row, from uniqueRowKeys. */
    rowKeys: string[];
    enableCallButton: boolean;
    /** The selection is the open page, not a picker's Accept target or the
     *  default item of a page that has no row, such as a conference. */
    selectionIsOpenPage: boolean;
    onSelect: (index: number, key: string, item?: PbxListItem) => void;
    /** Keeps one identity for the list's life. */
    onToggleSection: (
        department: keyof DepartmentState,
        isOpen: boolean,
    ) => void;
    onRowUnmountWithFocus: () => void;
}

interface Props {
    index: number;
    style: CSSProperties;
    data: PbxListItemData;
}

// Read when a row's menu opens. A row first renders before the list's
// elements are on the page, and it does not render again with the list.
const lockScroll: LockScroll = {
    get scroll() {
        return document.querySelector('.left-nav-scroll');
    },
    get wrapper() {
        return document.querySelector('.left-nav-scroll-wrapper');
    },
};

interface ItemContentProps {
    Component: ComponentType<any>;
    itemKey: string;
    item?: PbxListItem;
    marking?: RowMarking;
    enableCallButton: boolean;
    onClick: MouseEventHandler;
    lockScroll: LockScroll;
}

const PbxListItemContent = ({ Component, ...props }: ItemContentProps) => (
    <Component {...props} />
);

// A refetch can write an item again, equal but new: RESULT_QUEUES does so for
// every queue. So the item counts as changed only when its value does. Every
// other prop keeps its identity while it says the same.
const itemContentPropsAreEqual = (
    previous: ItemContentProps,
    next: ItemContentProps,
) =>
    is(previous.item, next.item) &&
    (Object.keys(next) as (keyof ItemContentProps)[]).every(
        (prop) => prop === 'item' || Object.is(previous[prop], next[prop]),
    );

// The item renders again only when one of its own props changes. A change to
// the list's rows, such as a new favourite, a folded section or a new search
// text, renders this row's frame again, and the frame stops here.
const MemoPbxListItemContent = React.memo(
    PbxListItemContent,
    itemContentPropsAreEqual,
);

const SectionTitle = React.memo(FoldableSectionTitle);

interface ItemRowProps {
    index: number;
    row: Extract<Row, { kind: 'item' }>;
    data: PbxListItemData;
}

const PbxListItemRow = ({ index, row, data }: ItemRowProps) => {
    const isSelected = useStoreHoldsRow(
        data.selectionStore,
        data.rowKeys[index],
    );
    const { Component, key, item } = row;
    const marking = data.selectionIsOpenPage ? 'pageOpen' : 'highlighted';
    // The list renders this row again whenever its index or the item data
    // change, so a click acts on the row where it is now, with its current
    // item, also after the list sorts again.
    const onClick = useStableCallback(() => data.onSelect(index, key, item));

    return (
        <MemoPbxListItemContent
            Component={Component}
            item={item}
            itemKey={key}
            marking={isSelected ? marking : undefined}
            enableCallButton={data.enableCallButton}
            onClick={onClick}
            lockScroll={lockScroll}
        />
    );
};

const RowContent = ({ index, data }: Omit<Props, 'style'>) => {
    const row = data.rows[index];

    switch (row.kind) {
        case 'sectionTitle':
            return (
                <SectionTitle
                    sectionId={row.department}
                    title={row.title}
                    isOpen={row.isOpen}
                    canToggle={row.canToggle}
                    onToggle={data.onToggleSection}
                />
            );
        case 'item':
            return <PbxListItemRow index={index} row={row} data={data} />;
        default:
            return null;
    }
};

const PbxListRow = ({ index, style, data }: Props) => (
    <ListRowFrame
        index={index}
        style={style}
        isFocusable={rowIsFocusable(data.rows[index])}
        tabStopStore={data.tabStopStore}
        tabStopKey={data.rowKeys[index]}
        onUnmountWithFocus={data.onRowUnmountWithFocus}
    >
        <RowContent index={index} data={data} />
    </ListRowFrame>
);

export default React.memo(PbxListRow, areEqual);
